import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { CREATE_ORDER_SIDE, DEPTH, SOCKET_SUB_TYPE, SOCKET_TOPICS } from 'src/common/constants';
import {
    currencyFormatter,
    digits,
    digits2,
    digits3,
    isEmptyObj,
    removeCommas,
    roundNumber,
} from 'src/common/helpers';
import { Spinner, Tooltip } from 'src/components/core';
import { BuySell, BuySellSingle, CrossCircleSmall, ExpandOut } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { Select, SelectItem } from 'src/components/core/select';
import OrderBookRatio from 'src/components/features/common/components/OrderBookRatio';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
    cancelActiveOrder,
    fetchAvailableAssets,
    updateCreateOrderPrice,
} from 'src/setup/redux/slices/derivativesSlice';
import SocketContext from 'src/setup/socket/socketContext';
import { ActiveOrder, FutureSymbol, TradeSocketResponseData } from 'src/types';

import {
    ListHeader,
    ListItem,
    ListItemContainer,
    ListTitle,
    TokenPriceContainer,
    TokenPriceItem,
    OrderBookContainer,
} from './list';

interface OrderBookProps {
    isTradeExpand?: boolean;
    setExpandView?: () => void;
}

interface OrderBookItem {
    price: string;
    amount: string;
    total: string;
}
interface CountShadowResult {
    a: OrderBookItem[];
    b: OrderBookItem[];
    maxQty: number;
}
const BOOK_ORIENTATION = {
    SELL_BUY: 1,
    BUY_SELL: 2,
    BUY_ONLY: 3,
    SELL_ONLY: 4,
};
const OrderBook: FC<OrderBookProps> = ({ isTradeExpand, setExpandView }) => {
    const { t } = useTranslation();
    const { isMobile, colors } = useTheme();
    const bookRef = useRef<HTMLDivElement>(null);
    const tooltipActiveRef = useRef<HTMLElement>(null);
    const tooltipCancelRef = useRef<HTMLElement>(null);
    const symbolId = useAppSelector((state) => state.derivatives.currentSymbol.symbolId);
    const { mergedDepth, indexPrice } = useAppSelector((state) => state.socket);
    const { futuresSymbol } = useAppSelector((state) => state.global.configV2);
    const { slowBroker } = useAppSelector((state) => state.socket);
    const { activeOrders, currentSymbol } = useAppSelector((state) => state.derivatives);
    const cancelActiveOrderLoading = useAppSelector(
        (state) => state.derivatives.loading.cancelActiveOrder,
    );
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);
    const [tokenInfo, setTokenInfo] = useState({} as TradeSocketResponseData);
    const [symbol, setSymbol] = useState({} as FutureSymbol);
    const [bookOrientation, setBookOrientation] = useState(BOOK_ORIENTATION.SELL_BUY);
    const [digit, setDigit] = useState('0');
    const [limit, setLimit] = useState(22);
    const [digitMerge, setDigitMerge] = useState<string[]>([]);
    const [indexPriceState, setIndexPriceState] = useState({ class: 'up', price: '--' });
    const [orderBook, setOrderBook] = useState({
        a: [],
        b: [],
        maxQty: 0,
    } as CountShadowResult);
    const [activeOrderPrices, setActiveOrderPrices] = useState([] as string[]);
    const [rowHovered, setRowHovered] = useState('');
    const [activeHovered, setActiveHovered] = useState('');
    const [cancelHovered, setCancelHovered] = useState('');
    const dispatch = useAppDispatch();

    const { reSubscribeTopic } = useContext(SocketContext);

    useEffect(() => {
        const book = bookRef.current;
        if (book && book?.scrollTop === 0 && book?.dataset.scrolled !== 'true') {
            book.scrollTop = book.scrollHeight - book.clientHeight;
            if (book.scrollTop !== 0) {
                book.dataset.scrolled = true.toString();
            }
        }
    }, [orderBook]);

    useEffect(() => {
        const book = bookRef.current;
        if (book && bookOrientation === BOOK_ORIENTATION.BUY_ONLY) {
            book.scrollTop = 0;
        } else if (book) {
            book.scrollTop = book.scrollHeight - book.clientHeight;
        }
    }, [bookOrientation]);

    useEffect(() => {
        setTokenInfo(slowBroker[symbolId]);
    }, [slowBroker, symbolId]);

    useEffect(() => {
        if (futuresSymbol === undefined || futuresSymbol === null) return;
        const symbolObj: FutureSymbol = futuresSymbol[symbolId];
        if (symbolObj === undefined || symbolObj === null) return;
        const digitMergeArr = symbolObj?.digitMerge.split(',');
        setDigit(digitMergeArr[0]);
        setDigitMerge(digitMergeArr);
        setSymbol(symbolObj);
    }, [futuresSymbol, symbolId]);

    useEffect(() => {
        if (!mergedDepth || isEmptyObj(mergedDepth) || isEmptyObj(symbol)) return;
        const processDepth = (depth: string[][]) => {
            const processedDepth: OrderBookItem[] = [];
            let totalQty = 0;
            let maxQty = 0;
            depth.forEach((item) => {
                if (item && digit) {
                    const price = Number(item[0]);
                    const amount = Number(item[1]);
                    totalQty += amount;
                    if (amount > maxQty) {
                        maxQty = amount;
                    }
                    processedDepth.push({
                        price: currencyFormatter(digits(price, DEPTH[digit]), DEPTH[digit]),
                        amount: roundNumber(amount, DEPTH[symbol?.basePrecision]).toFixed(
                            DEPTH[symbol?.basePrecision],
                        ),
                        total: currencyFormatter(
                            digits(totalQty, DEPTH[symbol?.basePrecision]),
                            DEPTH[symbol?.basePrecision],
                        ),
                    });
                }
            });
            if (processedDepth.length > limit) {
                processedDepth.length = limit;
            }
            return { processedDepth, maxQty };
        };

        const { processedDepth: a, maxQty: maxQtyA } = processDepth(mergedDepth.a);
        const { processedDepth: b, maxQty: maxQtyB } = processDepth(mergedDepth.b);

        const maxQty = Math.max(maxQtyA, maxQtyB);

        if (
            (bookOrientation === BOOK_ORIENTATION.SELL_BUY ||
                bookOrientation === BOOK_ORIENTATION.SELL_ONLY) &&
            !isMobile
        ) {
            a.reverse();
        }
        if (BOOK_ORIENTATION.BUY_SELL === bookOrientation) {
            b.reverse();
        }

        setOrderBook({ a, b, maxQty });
    }, [digit, mergedDepth, symbol, limit, bookOrientation, isMobile]);

    useEffect(() => {
        const prevPrice = currencyFormatter(
            digits(Number(indexPrice.prevPrice), DEPTH[symbol?.minPricePrecision]),
            DEPTH[symbol?.minPricePrecision],
            DEPTH[symbol?.minPricePrecision],
        );
        const newPrice = currencyFormatter(
            digits(Number(indexPrice.newPrice), DEPTH[symbol?.minPricePrecision]),
            DEPTH[symbol?.minPricePrecision],
            DEPTH[symbol?.minPricePrecision],
        );
        if (prevPrice > newPrice) {
            setIndexPriceState({ class: 'down', price: newPrice });
        } else if (prevPrice < newPrice) {
            setIndexPriceState({ class: 'up', price: newPrice });
        } else {
            setIndexPriceState((prevIndexPrice) => ({
                ...prevIndexPrice,
                price: newPrice,
            }));
        }
    }, [indexPrice, symbol?.minPricePrecision]);

    useEffect(() => {
        let ordersPrices: string[] = [];
        if (activeOrders.length > 0) {
            activeOrders.map((order: ActiveOrder) => {
                if (order.symbolId === currentSymbol.symbolId) {
                    if (order.side.startsWith(CREATE_ORDER_SIDE.SELL)) {
                        ordersPrices.push(
                            currencyFormatter(digits2(order.price, DEPTH[digit]), DEPTH[digit]),
                        );
                    } else {
                        ordersPrices.push(
                            currencyFormatter(digits3(order.price, DEPTH[digit]), DEPTH[digit]),
                        );
                    }
                }
            });
        }
        setActiveOrderPrices(ordersPrices);
    }, [activeOrders, currentSymbol.symbolId, digit]);

    const setDigitAndUpdateTopic = (value: string) => {
        const aggTradeDigits = DEPTH[value];
        reSubscribeTopic(SOCKET_SUB_TYPE.TRADE_DATA, {
            [SOCKET_TOPICS.MERGED_DEPTH]: {
                params: {
                    dumpScale: aggTradeDigits <= 0 ? aggTradeDigits - 1 : aggTradeDigits,
                },
            },
        });
        setDigit(value);
    };

    const orderCancelClicked = (orderDirection: string, orderPrice: string) => {
        const orderSide = orderDirection === 'up' ? 'BUY' : 'SELL';
        activeOrders.map((order: ActiveOrder) => {
            const currentOrderPrice = currencyFormatter(
                orderSide === CREATE_ORDER_SIDE.SELL
                    ? digits2(order.price, DEPTH[digit])
                    : digits(order.price, DEPTH[digit]),
                DEPTH[digit],
            );
            if (order.side.indexOf(orderSide) > -1 && currentOrderPrice === orderPrice) {
                dispatch(
                    cancelActiveOrder({
                        order_id: order.orderId,
                        client_order_id: new Date().getTime(),
                        type: order.type,
                        account_index: userCurrentFutureTradeAccountIndex,
                    }),
                ).then(() => {
                    dispatch(
                        fetchAvailableAssets({ account_index: userCurrentFutureTradeAccountIndex }),
                    );
                });
            }
        });
    };

    const renderWall = (bookData: OrderBookItem[], directionClass: string, isMobile = false) => {
        let bgClass = 'rgba(246, 92, 92, 0.08)';
        let bgRight = false;

        if (directionClass === 'up') {
            bgClass = 'rgba(62, 183, 114, 0.08)';
            bgRight = true;
        }
        if (!isMobile) {
            bgRight = true;
        }

        return bookData.map((item: OrderBookItem, index) => {
            const ratio = (parseFloat(item.amount) / orderBook.maxQty) * 100;
            const width = Math.min(ratio, 100);
            const isRowHovered = `${directionClass}-${index}` === rowHovered;
            const isActiveHovered = `${directionClass}-active-${index}` === activeHovered;
            const isCancelHovered = `${directionClass}-cancel-${index}` === cancelHovered;
            const isUserOrder = activeOrderPrices.includes(item.price);
            const parentMouseEnter = () => {
                if (isUserOrder) {
                    setRowHovered(`${directionClass}-${index}`);
                }
            };
            const parentMouseLeave = () => {
                if (isRowHovered) {
                    setRowHovered('');
                }
            };
            const activeMouseEnter = () => {
                setActiveHovered(`${directionClass}-active-${index}`);
            };
            const activeMouseLeave = () => {
                if (isActiveHovered) {
                    setActiveHovered('');
                }
            };
            const cancelMouseEnter = () => {
                setCancelHovered(`${directionClass}-cancel-${index}`);
            };
            const cancelMouseLeave = () => {
                if (isCancelHovered) {
                    setCancelHovered('');
                }
            };

            return (
                <ListItem
                    key={index}
                    onMouseEnter={parentMouseEnter}
                    onMouseLeave={parentMouseLeave}
                    onClick={() => dispatch(updateCreateOrderPrice(removeCommas(item.price)))}
                    className={bgRight ? 'right' : 'left'}
                    isTradeExpand={isTradeExpand}
                >
                    <div
                        style={{
                            width: `${width}%`,
                            background: bgClass,
                        }}
                    ></div>
                    <span className={directionClass}>
                        {item.price}
                        {isUserOrder && (
                            <Tooltip
                                overlay={t('order_book_active_order')}
                                toolTipRef={tooltipActiveRef}
                                visible={isActiveHovered}
                                onMouseEnter={activeMouseEnter}
                                onMouseLeave={activeMouseLeave}
                            >
                                <span>&nbsp;•</span>
                            </Tooltip>
                        )}
                    </span>
                    {!isMobile && <span>{item.amount}</span>}
                    <span>
                        {item.total}
                        {isRowHovered && cancelActiveOrderLoading && (
                            <>
                                &nbsp;
                                <Spinner size="9" />
                            </>
                        )}
                        {isRowHovered && !cancelActiveOrderLoading && (
                            <Tooltip
                                overlay={t('order_book_cancel_order')}
                                visible={isCancelHovered}
                                toolTipRef={tooltipCancelRef}
                                onMouseEnter={cancelMouseEnter}
                                onMouseLeave={cancelMouseLeave}
                                className={'cancel-order-tooltip'}
                            >
                                <>
                                    &nbsp;
                                    <CrossCircleSmall
                                        stroke={colors.brand.main}
                                        fill={colors.text.tertiary}
                                        onClick={() =>
                                            orderCancelClicked(directionClass, item.price)
                                        }
                                        style={{ cursor: 'pointer' }}
                                    />
                                </>
                            </Tooltip>
                        )}
                    </span>
                </ListItem>
            );
        });
    };

    return !isMobile ? (
        <OrderBookContainer isTradeExpand={isTradeExpand}>
            <ListHeader isTradeExpand={isTradeExpand}>
                <FlexContainer
                    styles={{
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'flex-start',
                    }}
                >
                    <span>{t('order_book')}</span>
                    <ExpandOut
                        stroke={colors.text.tertiary}
                        style={{ cursor: 'pointer' }}
                        onClick={setExpandView}
                    />
                </FlexContainer>
                <FlexContainer styles={{ justifyContent: 'space-between', width: '100%' }}>
                    <FlexContainer className={'sorting-icons'}>
                        <Tooltip overlay={t('full_order_book')}>
                            <BuySell
                                direction="sell"
                                style={{ cursor: 'pointer' }}
                                active={BOOK_ORIENTATION.SELL_BUY === bookOrientation}
                                onClick={() => {
                                    setBookOrientation(BOOK_ORIENTATION.SELL_BUY);
                                    setLimit(22);
                                }}
                            />
                        </Tooltip>
                        <Tooltip overlay={t('full_order_book')}>
                            <BuySell
                                active={BOOK_ORIENTATION.BUY_SELL === bookOrientation}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setBookOrientation(BOOK_ORIENTATION.BUY_SELL);
                                    setLimit(22);
                                }}
                            />
                        </Tooltip>
                        <Tooltip overlay={t('buy')}>
                            <BuySellSingle
                                active={BOOK_ORIENTATION.BUY_ONLY === bookOrientation}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setBookOrientation(BOOK_ORIENTATION.BUY_ONLY);
                                    setLimit(35);
                                }}
                            />
                        </Tooltip>
                        <Tooltip overlay={t('sell')}>
                            <BuySellSingle
                                direction="sell"
                                active={BOOK_ORIENTATION.SELL_ONLY === bookOrientation}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                    setBookOrientation(BOOK_ORIENTATION.SELL_ONLY);
                                    setLimit(35);
                                }}
                            />
                        </Tooltip>
                    </FlexContainer>
                    <Select
                        classes={{
                            root: 'selectBox',
                            selected: 'selected',
                        }}
                        value={digit}
                        onChange={(value) => setDigitAndUpdateTopic(value)}
                    >
                        {digitMerge.map((item) => (
                            <SelectItem key={item} value={item}>
                                {item}
                            </SelectItem>
                        ))}
                    </Select>
                </FlexContainer>
            </ListHeader>
            <ListTitle isTradeExpand={isTradeExpand}>
                <span>{t('price')}</span>
                <span>{`${t('qty')} (${currentSymbol.secondLevelUnderlyingId})`}</span>
                <span>{`${t('total')} (${currentSymbol.secondLevelUnderlyingId})`}</span>
            </ListTitle>
            {BOOK_ORIENTATION.BUY_ONLY !== bookOrientation && (
                <ListItemContainer
                    className={
                        BOOK_ORIENTATION.SELL_BUY === bookOrientation ||
                        BOOK_ORIENTATION.SELL_ONLY === bookOrientation ||
                        BOOK_ORIENTATION.BUY_SELL === bookOrientation
                            ? 'content-end'
                            : ''
                    }
                    ref={bookRef}
                >
                    {BOOK_ORIENTATION.SELL_BUY === bookOrientation &&
                        renderWall(orderBook.a, 'down')}
                    {BOOK_ORIENTATION.BUY_SELL === bookOrientation && renderWall(orderBook.b, 'up')}
                    {BOOK_ORIENTATION.SELL_ONLY === bookOrientation &&
                        renderWall(orderBook.a, 'down')}
                </ListItemContainer>
            )}
            <TokenPriceContainer>
                <TokenPriceItem>
                    <span>{t('last_price')}</span>
                    <span
                        className={
                            Number(tokenInfo?.m) >= 0
                                ? 'up'
                                : Number(tokenInfo?.m) < 0
                                ? 'down'
                                : ''
                        }
                    >
                        {Number(tokenInfo?.c)
                            ? currencyFormatter(
                                  Number(tokenInfo.c),
                                  Number(DEPTH[symbol?.minPricePrecision || 0]),
                              )
                            : '--'}
                    </span>
                </TokenPriceItem>
                <TokenPriceItem>
                    <span>{t('index_price')}</span>
                    <span className={indexPriceState.class}>
                        {Number(removeCommas(indexPriceState.price)) ? indexPriceState.price : '--'}
                    </span>
                </TokenPriceItem>
            </TokenPriceContainer>
            {BOOK_ORIENTATION.SELL_ONLY !== bookOrientation && (
                <ListItemContainer>
                    {BOOK_ORIENTATION.BUY_SELL === bookOrientation &&
                        renderWall(orderBook.a, 'down')}
                    {BOOK_ORIENTATION.SELL_BUY === bookOrientation && renderWall(orderBook.b, 'up')}
                    {BOOK_ORIENTATION.BUY_ONLY === bookOrientation && renderWall(orderBook.b, 'up')}
                </ListItemContainer>
            )}
            <OrderBookRatio />
        </OrderBookContainer>
    ) : (
        <OrderBookContainer>
            <ListHeader>
                <Select
                    classes={{
                        root: 'selectBox',
                        selected: 'selected',
                    }}
                    value={digit}
                    onChange={(value) => setDigitAndUpdateTopic(value)}
                >
                    {digitMerge.map((item) => (
                        <SelectItem key={item} value={item}>
                            {item}
                        </SelectItem>
                    ))}
                </Select>
            </ListHeader>
            <ListTitle>
                <span>{`${t('qty')} (${currentSymbol.secondLevelUnderlyingId})`}</span>
                <span>{t('price')}</span>
                <span>{`${t('qty')} (${currentSymbol.secondLevelUnderlyingId})`}</span>
            </ListTitle>
            <ListItemContainer>
                <div>{renderWall(orderBook.b, 'up', true)}</div>
                <div>{renderWall(orderBook.a, 'down', true)}</div>
            </ListItemContainer>
            <OrderBookRatio />
        </OrderBookContainer>
    );
};

export { OrderBook };
